import { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Trash2 } from "lucide-react";
import { EditableGroupName } from "../ElementInspectorV3/EditableGroupName";
import { LvMatchValue } from "../LvMatchValue";
import { LvMatchEditor } from "../LvMatchEditor";
import { Button } from "components/common/Button";
import type { InspectorHeaderProps } from "./types";

const gridCols: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
};

export const InspectorHeader = ({
  canEdit,
  groupName,
  groupTypeOptions,
  groupColor,
  onRenameGroup,
  nameStatus,
  onMarkReviewed,
  onToggleSuspicious,
  countLabel,
  count,
  lvMatch,
  boqPositions,
  onSaveLvMatch,
  isTraversing,
  onStartTraversing,
  onStopTraversing,
  isGroupNameEditable = true,
  isLvMatchVisible = true,
  isLvMatchEditable = true,
  onDeleteGroup,
  dataOnboarding,
  groupNameDataOnboarding,
  startReviewDataOnboarding,
}: InspectorHeaderProps) => {
  const { t } = useTranslation();
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);

  const showStartReview = !isEditingGroupName;
  const gridColumnCount =
    1 + Number(isLvMatchVisible) + Number(showStartReview);

  return (
    <div
      data-onboarding={dataOnboarding}
      className="shrink-0 px-4 pt-2 pb-3 border-b border-blue-200 space-y-3 bg-blue-50"
    >
      {isGroupNameEditable && (
        <EditableGroupName
          canEdit={canEdit}
          groupName={groupName}
          groupTypeOptions={groupTypeOptions}
          groupColor={groupColor}
          onRename={onRenameGroup}
          nameStatus={nameStatus}
          onMarkReviewed={onMarkReviewed}
          onToggleSuspicious={onToggleSuspicious}
          onEditingChange={setIsEditingGroupName}
          dataOnboarding={groupNameDataOnboarding}
        />
      )}
      {!isEditingGroupName && canEdit && onDeleteGroup && (
        <div className={clsx("flex items-center justify-start gap-2")}>
          <Button
            variant="text"
            size="sm"
            onClick={onDeleteGroup}
            startAdornment={<Trash2 size={14} />}
            className="shrink-0 !p-0 text-red-600 hover:text-red-700"
          >
            {t("smartSurveyor.groupInspector.deleteGroup")}
          </Button>
          <span className="h-3.5 w-px shrink-0 bg-gray-300" />
          <span className="text-xs text-gray-500">
            {t("smartSurveyor.groupInspector.deleteGroupHint")}
          </span>
        </div>
      )}
      {isEditingGroupName && <hr className="border-blue-200" />}
      <div className={clsx("grid gap-2", gridCols[gridColumnCount])}>
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-center">
          <span className="block text-xs font-medium text-gray-700">
            {countLabel}
          </span>
          <span className="block text-sm font-bold text-gray-800 mt-0.5">
            {count}
          </span>
        </div>
        {isLvMatchVisible && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-center min-w-0">
            <span className="block text-xs font-medium text-gray-700">
              {t("smartSurveyor.groupInspector.lvPosition")}
            </span>
            {canEdit &&
            boqPositions &&
            boqPositions.length > 0 &&
            isLvMatchEditable ? (
              <LvMatchEditor
                lvMatch={lvMatch}
                boqPositions={boqPositions}
                onSave={onSaveLvMatch}
              />
            ) : (
              <LvMatchValue lvMatch={lvMatch} />
            )}
          </div>
        )}
        {showStartReview && (
          <Button
            variant={isTraversing ? "primary" : "success"}
            disabled={count === 0}
            onClick={isTraversing ? onStopTraversing : onStartTraversing}
            data-onboarding={startReviewDataOnboarding}
          >
            {isTraversing
              ? t("smartSurveyor.elementInspector.reviewLater")
              : t("smartSurveyor.elementInspector.startReview")}
          </Button>
        )}
      </div>
    </div>
  );
};
